import { Component } from "@theme/component";

/**
 * A custom element that manages tabs.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} header
 * @property {HTMLElement} indicator
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class TabsComponent extends Component {
  requiredRefs = ["header", "indicator"];

  get tabs() {
    const tabs = this.refs.tabs;
    if (Array.isArray(tabs)) {
      return tabs.filter((tab) => tab instanceof HTMLButtonElement);
    }
    
    // Fallback when refs are scoped to nested components.
    return Array.from(this.querySelectorAll(".tabs__tab")).filter(
      (tab) => tab instanceof HTMLButtonElement,
    );
  }

  get panels() {
    const panels = this.refs.panels;
    if (Array.isArray(panels)) {
      return panels.filter((panel) => panel instanceof HTMLElement);
    }

    return Array.from(this.querySelectorAll(".tabs__panel")).filter(
      (panel) => panel instanceof HTMLElement,
    );
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.tabs.length) return;

    this.onTabClick = this.onTabClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", this.onTabClick);
      tab.addEventListener("keydown", this.onKeyDown);
    });

    window.addEventListener("resize", this.onWindowResize);

    const activeTab = this.getActiveTab();
    if (activeTab) {
      this.setActiveTab(activeTab);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.tabs.forEach((tab) => {
      tab.removeEventListener("click", this.onTabClick);
      tab.removeEventListener("keydown", this.onKeyDown);
    });

    window.removeEventListener("resize", this.onWindowResize);
  }

  getActiveTab() {
    return (
      this.tabs.find((tab) => tab.classList.contains("active")) || this.tabs[0]
    );
  }

  /**
   * @param {Event} event
   */
  onTabClick(event) {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLButtonElement)) return;
    this.setActiveTab(tab);
  }

  /**
   * @param {Event} event
   */
  onKeyDown(event) {
    if (!(event instanceof KeyboardEvent)) return;

    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    event.preventDefault();

    const currentTab = event.currentTarget;
    if (!(currentTab instanceof HTMLButtonElement)) return;

    if (!this.tabs.length) return;
    const currentIndex = this.tabs.indexOf(currentTab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    if (event.key === "ArrowRight")
      nextIndex = (currentIndex + 1) % this.tabs.length;
    if (event.key === "ArrowLeft")
      nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = this.tabs.length - 1;

    const nextTab = this.tabs[nextIndex];
    if (!nextTab) return;

    this.setActiveTab(nextTab);
    nextTab.focus();
  }

  onWindowResize() {
    const activeTab = this.getActiveTab();
    if (activeTab) {
      this.updateIndicator(activeTab);
    }
  }

  /**
   * Activate selected tab + panels
   * @param {HTMLButtonElement} selectedTab
   */
  setActiveTab(selectedTab) {
    if (!this.tabs.length) return;
    const selectedIndex = this.tabs.indexOf(selectedTab);
    if (selectedIndex < 0) return;

    this.tabs.forEach((tab, index) => {
      const isActive = index === selectedIndex;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    if (this.panels.length) {
      this.panels.forEach((panel) => {
        const isActive = panel.id === selectedTab.getAttribute("aria-controls");
        panel.classList.toggle("active", isActive);
        panel.hidden = !isActive;
      });
    }

    this.updateIndicator(selectedTab);
  }

  /**
   * Update underline indicator position
   * @param {HTMLButtonElement} activeTab
   */
  updateIndicator(activeTab) {
    if (!activeTab) return;

    // move indicator INTO active tab
    activeTab.appendChild(this.refs.indicator);

    this.refs.indicator.style.width = `${activeTab.offsetWidth}px`;
  }
}

if (!customElements.get("tabs-component")) {
  customElements.define("tabs-component", TabsComponent);
}